import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, MessageSquareHeart, PlayCircle } from "lucide-react";
import api, { API } from "@/lib/api";

export function youtubeId(url) {
  const m = (url || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
}

export default function TestimonialsCarousel({ enabled }) {
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    api.get("/testemunhos").then((r) => setItems(r.data)).catch(() => {});
  }, [enabled]);

  if (!enabled || items.length === 0) return null;

  const scroll = (dir) => {
    const el = ref.current;
    if (!el) return;
    const card = el.firstElementChild;
    el.scrollBy({ left: dir * ((card?.clientWidth || 320) + 20), behavior: "smooth" });
  };

  return (
    <section data-testid="testimonials-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <p className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.14em] uppercase text-sky-700">
            <MessageSquareHeart className="w-4 h-4" /> Testemunhos
          </p>
          <h2 className="font-heading mt-1 text-base md:text-lg font-bold text-slate-900">Veja o que Deus tem feito através de cada contribuição</h2>
        </div>
        {items.length > 1 && (
          <div className="hidden sm:flex gap-2">
            <button type="button" data-testid="testimonials-prev" onClick={() => scroll(-1)} aria-label="Anterior" className="w-11 h-11 rounded-full bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-700 shadow-sm transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button type="button" data-testid="testimonials-next" onClick={() => scroll(1)} aria-label="Próximo" className="w-11 h-11 rounded-full bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-700 shadow-sm transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
      <div ref={ref} data-testid="testimonials-track" className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin">
        {items.map((t) => (
          <TestimonialCard key={t.id} t={t} />
        ))}
      </div>
    </section>
  );
}

function TestimonialCard({ t }) {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const yt = youtubeId(t.video_url);
  const capa = t.capa ? `${API}/files/${t.capa}?v=${encodeURIComponent(t.updated_at)}` : null;
  const showCover = !playing && (capa || yt);
  return (
    <article data-testid={`testimonial-card-${t.id}`} className="snap-start shrink-0 w-[85vw] sm:w-[360px] bg-white rounded-3xl border border-slate-200/80 p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_-16px_rgba(15,42,107,0.25)]">
      <div className="relative aspect-[9/12] sm:aspect-[4/5] rounded-2xl overflow-hidden bg-black">
        {yt ? (
          (playing || !capa) && (
            <iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube-nocookie.com/embed/${yt}?rel=0${playing ? "&autoplay=1" : ""}`} title={t.titulo} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          )
        ) : (
          <>
            <video ref={videoRef} className="absolute inset-0 w-full h-full object-contain" src={`${API}/testemunhos/${t.id}/video?v=${encodeURIComponent(t.updated_at)}`} poster={capa || undefined} controls={playing} playsInline preload="metadata" onPlay={() => setPlaying(true)} />
            {!capa && !playing && (
              <button type="button" data-testid={`testimonial-play-${t.id}`} onClick={() => { setPlaying(true); videoRef.current?.play(); }} className="absolute inset-0 w-full h-full bg-gradient-to-br from-[#0f2a6b] via-[#12306f]/80 to-black/60 flex items-center justify-center group" aria-label={`Assistir ${t.titulo}`}>
                <span className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white group-hover:scale-105 transition-[transform,background-color] flex items-center justify-center shadow-xl">
                  <PlayCircle className="w-10 h-10 text-[#0f2a6b]" />
                </span>
              </button>
            )}
          </>
        )}
        {showCover && capa && (
          <button type="button" data-testid={`testimonial-cover-${t.id}`} onClick={() => { setPlaying(true); videoRef.current?.play(); }} className="absolute inset-0 w-full h-full group" aria-label={`Assistir ${t.titulo}`}>
            <img src={capa} alt={t.titulo} className="absolute inset-0 w-full h-full object-cover" />
            <span className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white group-hover:scale-105 transition-[transform,background-color] flex items-center justify-center shadow-xl">
                <PlayCircle className="w-10 h-10 text-[#0f2a6b]" />
              </span>
            </span>
          </button>
        )}
        {!yt && !capa && !playing && null}
      </div>
      <div className="px-2 pt-3 pb-1">
        <p className="font-heading font-bold text-slate-900 leading-snug">{t.titulo}</p>
        {t.nome && <p className="text-sm text-slate-500 mt-0.5">{t.nome}</p>}
      </div>
    </article>
  );
}
